import { Button, DatePicker, Flex, Form, Select } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { useAppSelector } from "../../../../../../app/store";
import CustomModal from "../../../../../../components/ui/CustomModal";
import {
  AUTH_MANAGE_API,
  PRODUCT_ID,
} from "../../../../../../constants/constants";
import { useGetAllKPIGroupByJobQuery } from "../../../../../../features/organization/contractual/KPISetup/kpiSetupApi";
import { useGetAllJobPostQuery } from "../../../../../../features/organization/job/jobApi";
import { CustomModalProps } from "../../../../../../types";
import { selectArrayOption } from "../../../../../../utils/utils";
import CalanderIcon from "../../../../survey/templates/_icons/CalanderIcon";

const DownloadTemplate = ({ open, handleClose }: CustomModalProps) => {
  const { user, access_token } = useAppSelector((state) => state.auth);
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);

  // Watch payment type and start date
  const paymentType = Form.useWatch("payment_type", form);
  const payrollMonth = Form.useWatch("payroll_month", form);
  const startDate = Form.useWatch("start_date", form);
  const jobId = Form.useWatch("job_id", form);

  const start_date_payroll = payrollMonth
    ? dayjs(payrollMonth).startOf("month").format("YYYY-MM-DD")
    : null;

  const end_date_payroll = payrollMonth
    ? dayjs(payrollMonth).endOf("month").format("YYYY-MM-DD")
    : null;

  const { data: jobResponse } = useGetAllJobPostQuery({
    params: {
      job_category: "call_center",
      business_id: user?.business_id,
    },
  });

  const { data: kpiGroupResponse } = useGetAllKPIGroupByJobQuery(
    {
      id: jobId,
      args: {
        params: {
          payment_type: paymentType,
          start_date: start_date_payroll ?? undefined,
          end_date: end_date_payroll ?? undefined,
        },
      },
    },
    { skip: !jobId || !paymentType || !payrollMonth }
  );

  // Auto set end date based on payment type
  useEffect(() => {
    if (!startDate || !paymentType) return;

    let end: Dayjs | null = null;
    if (paymentType === "daily") {
      end = dayjs(startDate); // same day
    } else if (paymentType === "weekly") {
      end = dayjs(startDate).add(6, "day"); // next 7 days total
    } else if (paymentType === "monthly") {
      end = dayjs(startDate).add(1, "month").subtract(1, "day");
    }

    form.setFieldsValue({ end_date: end });
  }, [startDate, paymentType, form]);

  // Disable only future days
  const disabledDate = (current: Dayjs) => {
    if (!paymentType || !current) return false;

    const today = dayjs().endOf("day");

    // Disable all future days (after today)
    if (current.isAfter(today)) return true;

    // if (paymentType === "daily") {
    //   // All past days allowed (no limit), only future disabled
    //   return current.isAfter(today);
    // }

    // if (paymentType === "weekly") {
    //   const min = today.subtract(7, "day");
    //   return current.isAfter(min.startOf("day")) || current.isAfter(today);
    // }

    // if (paymentType === "monthly") {
    //   const min = today.subtract(1, "month");
    //   return current.isAfter(min.startOf("day")) || current.isAfter(today);
    // }

    return false;
  };

  // const onFinish = async (values: any) => {
  //   const {
  //     job_id,
  //     kpi_group_id,
  //     start_date: period_from,
  //     end_date: peruid_to,
  //     payment_type,
  //   } = values || {};

  //   try {
  //     const params = new URLSearchParams({
  //       period_from,
  //       peruid_to,
  //       payment_type,
  //     }).toString();
  //     fetch(
  //       `${AUTH_MANAGE_API}/v1/kpi-setup/v1/job/${job_id}/kpi-groups/${kpi_group_id}/export?${params}`,
  //       {
  //         method: "GET",
  //         headers: {
  //           Accept:
  //             "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  //         },
  //       },
  //     )
  //       .then((res) => {
  //         console.log(res.body);
  //         return res.blob();
  //       })
  //       .then((blob) => {
  //         const url = window.URL.createObjectURL(blob);
  //         console.log(blob);
  //         const a = document.createElement("a");
  //         a.href = url;
  //         a.download = "file.xlsx"; // give your file a name
  //         document.body.appendChild(a);
  //         a.click();
  //         a.remove();
  //         window.URL.revokeObjectURL(url);
  //       })
  //       .catch((err) => console.error(err));
  //   } catch (error: any) {
  //     console.error("Download failed:", error?.message || error);
  //   }
  // };

  const onFinish = async (values: any) => {
    const { job_id, kpi_group_id, payment_type } = values || {};
    const params = new URLSearchParams({
      period_from: dayjs(start_date_payroll).format("YYYY-MM-DD"),
      period_to: dayjs(end_date_payroll).format("YYYY-MM-DD"),
      payment_type: payment_type,
    });
    try {
      setLoading(true);
      // Build query params
      const response = await fetch(
        `${AUTH_MANAGE_API}/v1/kpi-setup/v1/job/${job_id}/kpi-groups/${kpi_group_id}/export?${params.toString()}`,
        {
          method: "GET",
          headers: {
            Accept:
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            "ngrok-skip-browser-warning": "true",
            Authorization: `Bearer ${access_token}`,
            ProductId: PRODUCT_ID || "16",
          },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch file");

      const blob = await response.blob();

      // Make sure blob has correct type
      const fileBlob = new Blob([blob], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(fileBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "KPI Performance.xlsx"; // give your file a proper name
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      handleClose && handleClose();
    } catch (error: any) {
      console.error("Download failed:", error?.message || error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <CustomModal
      footer={null}
      open={open}
      handleClose={() => {
        handleClose && handleClose();
        form.resetFields();
      }}
      title="Download Template"
    >
      <Form
        className="custom-form"
        layout="vertical"
        requiredMark="optional"
        size="large"
        onFinish={onFinish}
        form={form}
      >
        <Form.Item name="job_id" label="Select Job" className="mb-4">
          <Select
            showSearch
            allowClear
            size="large"
            placeholder="Select"
            optionFilterProp="label"
            options={selectArrayOption(
              jobResponse?.data?.jobs || [],
              "job_title",
              "id"
            )}
          />
        </Form.Item>

        <Form.Item
          name="payment_type"
          label="Payment Type"
          className="mb-4"
          initialValue="monthly"
        >
          <Select
            size="large"
            placeholder="Select"
            allowClear
            options={[
              // { label: "Daily", value: "daily" },
              // { label: "Weekly", value: "weekly" },
              { label: "Monthly", value: "monthly" },
            ]}
            onChange={() => form.resetFields(["start_date", "end_date"])}
          />
        </Form.Item>

        <Form.Item
          name="payroll_month"
          label="Select Month"
          className="mb-4"
          rules={[{ required: true, message: "Please select a month" }]}
        >
          <DatePicker
            picker="month"
            className="w-full"
            format="MM/YYYY"
            placeholder={"Select a Month"}
            suffixIcon={<CalanderIcon />}
            disabled={!paymentType}
            disabledDate={(current) => {
              // Disable current month and future months
              return current && current >= dayjs().startOf("month");
            }}
          />
        </Form.Item>

        <Form.Item name="kpi_group_id" label="KPI Group" className="mb-4">
          <Select
            showSearch
            size="large"
            placeholder="Select"
            optionFilterProp="label"
            options={selectArrayOption(
              kpiGroupResponse?.data?.groups || [],
              "group_name",
              "id"
            )}
            allowClear
            disabled={!payrollMonth || !jobId}
          />
        </Form.Item>

        {/* <div className="grid gap-4 items-end grid-cols-1 md:grid-cols-2">
          <Form.Item name="start_date" label="Start Date" className="mb-4">
            <DatePicker
              size="large"
              className="w-full"
              format={"DD/MM/YYYY"}
              disabled={!paymentType}
              placeholder={"dd/mm/yyyy"}
              disabledDate={disabledDate}
              allowClear
            />
          </Form.Item>

          <Form.Item name="end_date" label="End Date" className="mb-4">
            <DatePicker
              disabled
              size="large"
              className="w-full"
              format={"DD/MM/YYYY"}
              placeholder={"dd/mm/yyyy"}
              allowClear
            />
          </Form.Item>
        </div> */}

        <Flex align="center" gap={16}>
          <Button
            size="middle"
            className="h-10 min-w-[125px] normal-dark-button"
            onClick={() => {
              handleClose && handleClose();
              form.resetFields();
            }}
          >
            Cancel
          </Button>
          <Button
            loading={loading}
            size="middle"
            htmlType="submit"
            className="h-10 min-w-[125px]"
            type="primary"
          >
            Download Template
          </Button>
        </Flex>
      </Form>
    </CustomModal>
  );
};

export default DownloadTemplate;
